import { forwardRef, useCallback, useMemo } from 'react';

import { useTiptapEditor } from '../../../hooks/use-tiptap-editor';
import { parseShortcutKeys } from '../../../lib/tiptap-utils';
import type { UseColorHighlightConfig } from '../../tiptap-ui/color-highlight-button';
import { COLOR_HIGHLIGHT_SHORTCUT_KEY, useColorHighlight } from '../../tiptap-ui/color-highlight-button';
import { Badge } from '../../tiptap-ui-primitive/badge';
import type { ButtonProps } from '../../tiptap-ui-primitive/button';
import { Button } from '../../tiptap-ui-primitive/button';

export interface ColorHighlightButtonProps extends Omit<ButtonProps, 'type'>, UseColorHighlightConfig {
  text?: string;
  showShortcut?: boolean;
}

export function ColorHighlightShortcutBadge({
  shortcutKeys = COLOR_HIGHLIGHT_SHORTCUT_KEY,
}: {
  shortcutKeys?: string;
}) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

export const ColorHighlightButton = forwardRef<HTMLButtonElement, ColorHighlightButtonProps>(
  (
    {
      editor: providedEditor,
      highlightColor,
      text,
      hideWhenUnavailable = false,
      mode = 'mark',
      onApplied,
      showShortcut = false,
      onClick,
      children,
      style,
      ...buttonProps
    },
    ref,
  ) => {
    const { editor } = useTiptapEditor(providedEditor);
    const { isVisible, canColorHighlight, isActive, handleColorHighlight, label, shortcutKeys } = useColorHighlight({
      editor,
      highlightColor,
      label: text || `Toggle highlight (${highlightColor})`,
      hideWhenUnavailable,
      mode,
      onApplied,
    });

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        handleColorHighlight();
      },
      [handleColorHighlight, onClick],
    );

    const buttonStyle = useMemo(
      () =>
        ({
          ...style,
          '--highlight-color': highlightColor,
        }) as React.CSSProperties,
      [highlightColor, style],
    );

    if (!isVisible) {
      return null;
    }

    return (
      <Button
        type="button"
        data-style="ghost"
        data-active-state={isActive ? 'on' : 'off'}
        role="button"
        tabIndex={-1}
        disabled={!canColorHighlight}
        data-disabled={!canColorHighlight}
        aria-label={label}
        aria-pressed={isActive}
        tooltip={label}
        onClick={handleClick}
        style={buttonStyle}
        {...buttonProps}
        ref={ref}
      >
        {children ?? (
          <>
            <span
              className="tiptap-button-highlight"
              style={{ '--highlight-color': highlightColor } as React.CSSProperties}
            />
            {text && <span className="tiptap-button-text">{text}</span>}
            {showShortcut && <ColorHighlightShortcutBadge shortcutKeys={shortcutKeys} />}
          </>
        )}
      </Button>
    );
  },
);

ColorHighlightButton.displayName = 'ColorHighlightButton';
